import {
  ChatCustomBlockTypes,
  type AnalyticsEventType,
  type AvailableLocales,
  type ChatbotParams,
  type ChatFastAction,
  type ChatMessage,
  type Locale,
  type SatisfactionForm,
  type SurveyAnswer,
} from "@do/shared-types";
import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { hasStepsFormBlock } from "../lib/merge-bot-messages";

export type ChatContextType = {
  autoScroll?: boolean;
  chatbotParams: ChatbotParams;
  satisfactionForm?: SatisfactionForm | null;
  // Persists a satisfaction survey answer (rating, then optionally feedback text).
  // Optional so previews / hosts without analytics can omit it.
  onSubmitSurvey?: (surveyAnswer: SurveyAnswer) => void | Promise<void>;
  // Called once when the user rates the conversation badly and the survey asks for the
  // redirection (`redirection_bad_rating`): the host sends the conversation on to the
  // standard "bad feedback" node. Optional so previews / hosts without a bot can omit it.
  onBadRating?: () => void | Promise<void>;
  // Records a generic analytics event (e.g. survey_displayed) for the session.
  // Optional so previews / hosts without analytics can omit it.
  onTrackEvent?: (event: {
    type: AnalyticsEventType;
    properties?: Record<string, unknown>;
  }) => void | Promise<void>;
  // Event types already recorded for the current session, used to avoid re-firing
  // once-per-session behaviours (e.g. showing the satisfaction survey only once).
  recordedEventTypes?: AnalyticsEventType[];
  // Optional container the satisfaction review dialog portals into (e.g. to keep it
  // clipped inside a preview instead of escaping to document.body).
  dialogPortalContainer?: HTMLElement | null;
  currentLocale?: Locale | null;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLTextAreaElement | null>;
  messagesRef: React.RefObject<HTMLDivElement | null>;
  lastMessageRef?: React.RefObject<HTMLDivElement | null>;
  messagesListRef: React.RefObject<HTMLDivElement | null>;
  isClosable?: boolean;
  isFullSize?: boolean;
  isOutOfCredits?: boolean;
  isPreviewMode?: boolean;
  isTestingMode?: boolean;
  isEditingMode?: boolean;
  lastMessageId?: string | null;
  lastUserMessageId?: string | null;
  lastUserMessageRef?: React.RefObject<HTMLDivElement | null>;
  mergedCtxData?: Record<string, unknown> | null;
  messages: ChatMessage[];
  noHeader?: boolean;
  noFooter?: boolean;
  noActions?: boolean;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  pendingCallConfirmation?: ChatFastAction | null;
  onConfirmCall?: () => void;
  onCancelCall?: () => void;
  onClose?: () => void;
  onChangeLocale?: (selectedLocale: Locale) => void;
  onRefreshConversation?: () => void;
  onHandoverRequest: () => void;
  onEditBotResponse?: (messageId: string, openDialog: boolean) => void;
  onFastAction: (resp: ChatFastAction) => void;
  /**
   * Agent testing tooling only: reports the `nodeId` of the hovered step of an `agentProcess`
   * block, `null` when the pointer leaves it, so the host can point at the matching element.
   */
  onAgentProcessStepHover?: (nodeId: string | null) => void;
  onSubmit: (answer: string) => void;
  /**
   * Rewrite a URL before it is opened/rendered. Identity by default. do-chat
   * supplies a transformation that applies `chatbotParams.urlRewriteConfig`.
   * Implementations MUST be defensive: a failure to rewrite must not throw.
   */
  rewriteUrl?: (url: string) => string;
  readonlyModeMessage?: string;
  isLiveChatActive?: boolean;
  // Timestamp set by the host when a livechat conversation ends. Used by the
  // satisfaction form `livechat_end_of_conversation` trigger.
  liveChatEndedAt?: number | null;
  isMobile?: boolean;
  isLoading?: boolean;
  maxUserMessageLength?: number;
  selectedLocale?: AvailableLocales[number] | null;
  defaultLocale?: AvailableLocales[number] | null;
  isAtBottom: boolean;
  setIsAtBottom: React.Dispatch<React.SetStateAction<boolean>>;
  scrollToBottom: (params?: { force?: boolean; smooth?: boolean }) => void;
  footerContent: React.ReactNode | null;
  setFooterContent: React.Dispatch<React.SetStateAction<React.ReactNode | null>>;
  buttonsContent: React.ReactNode | null;
  setButtonsContent: React.Dispatch<React.SetStateAction<React.ReactNode | null>>;
  canBackToPreviousStep: boolean;
  handleBackToPreviousStep: () => void;
  /**
   * Host-provided back navigation. When set, it overrides the built-in
   * message-slicing behaviour (used e.g. in `onlyLastMessage` mode where the
   * host keeps the conversation history instead of the `messages` array).
   */
  onBackToPreviousStep?: () => void;
  /**
   * Host-provided flag telling whether a previous step exists to go back to.
   * When defined, it drives the back button visibility instead of the
   * built-in check on the `messages` array.
   */
  hasPreviousStep?: boolean;
  /**
   * Host-provided message that back navigation must never remove, nor anything
   * before it — e.g. the end of a livechat, whose conversation cannot be
   * resumed by going back.
   */
  backStopMessageId?: string | null;
  getMessageResetKey: (messageKey?: string) => number;
  lastClickedButton: LastClickedButton;
  setLastClickedButton: React.Dispatch<React.SetStateAction<LastClickedButton>>;
  /**
   * Keys of the messages the list has already shown. It lives here, above the list, so that
   * a remounted list recognises a restored conversation — whatever it held when it mounted
   * is not necessarily old: the greeting often lands before the list is first drawn.
   */
  revealedMessageKeysRef?: React.MutableRefObject<Set<string>>;
};

export type LastClickedButton = { cuid?: string | null; type?: string | null } | null;

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

export const useOptionalChat = () => useContext(ChatContext);

type ChatProviderProps = {
  children: React.ReactNode;
} & Omit<
  ChatContextType,
  | "footerContent"
  | "setFooterContent"
  | "buttonsContent"
  | "setButtonsContent"
  | "canBackToPreviousStep"
  | "handleBackToPreviousStep"
  | "getMessageResetKey"
  | "lastClickedButton"
  | "setLastClickedButton"
  | "revealedMessageKeysRef"
>;

const getStepsResetMessageKey = (message: ChatMessage, fallbackIndex?: number) => {
  const stepsBlockId = message.blocks?.find(
    (block) => block.type === ChatCustomBlockTypes.steps
  )?.id;

  return (
    message.id ??
    stepsBlockId ??
    (fallbackIndex !== undefined ? `steps-message-${fallbackIndex}` : undefined)
  );
};

const getBackToPreviousStepTarget = (messages: ChatMessage[]) => {
  const lastUserIndex = messages.findLastIndex((message) => message.user);
  if (lastUserIndex === -1) return null;

  const botIndexesAfterLastUser = messages.reduce<number[]>((indexes, message, index) => {
    if (index > lastUserIndex && !message.user && !message.advisor) {
      indexes.push(index);
    }
    return indexes;
  }, []);

  for (let i = botIndexesAfterLastUser.length - 2; i >= 0; i--) {
    const botIndex = botIndexesAfterLastUser[i];
    if (hasStepsFormBlock(messages[botIndex])) {
      return {
        sliceEnd: botIndex + 1,
        resetMessageKey: getStepsResetMessageKey(messages[botIndex], botIndex),
      };
    }
  }

  const previousMessageIndex = lastUserIndex - 1;
  const previousMessage = previousMessageIndex >= 0 ? messages[previousMessageIndex] : undefined;

  return {
    sliceEnd: lastUserIndex,
    resetMessageKey:
      previousMessage && hasStepsFormBlock(previousMessage)
        ? getStepsResetMessageKey(previousMessage, previousMessageIndex)
        : undefined,
  };
};

export const ChatProvider = memo(
  ({
    children,
    selectedLocale,
    defaultLocale,
    chatbotParams,
    lastUserMessageId,
    lastUserMessageRef,
    messagesListRef,
    ...props
  }: ChatProviderProps) => {
    const selectedLocaleValues = useMemo(() => {
      if (selectedLocale) return selectedLocale;
      return chatbotParams.availableLocales && props.currentLocale
        ? chatbotParams.availableLocales?.[props.currentLocale]
        : null;
    }, [chatbotParams.availableLocales, props.currentLocale, selectedLocale]);

    const defaultLocaleValues = useMemo(() => {
      if (defaultLocale) return defaultLocale;
      return (
        chatbotParams.availableLocales &&
        Object.values(chatbotParams.availableLocales).find((locale) => locale.isDefault)
      );
    }, [chatbotParams.availableLocales, defaultLocale]);

    const { messages, setMessages, onBackToPreviousStep, backStopMessageId, isLiveChatActive } =
      props;

    const [footerContent, setFooterContent] = useState<React.ReactNode | null>(null);
    const [buttonsContent, setButtonsContent] = useState<React.ReactNode | null>(null);
    const [messageResetKeys, setMessageResetKeys] = useState<Record<string, number>>({});
    const [lastClickedButton, setLastClickedButton] = useState<LastClickedButton>(null);
    const revealedMessageKeysRef = useRef<Set<string>>(new Set());

    // Nothing before a livechat can be reached by going back, as the advisor
    // conversation cannot be resumed: back is off while it runs, and once it is
    // over it stops at the host-provided message marking its end.
    const backTarget = useMemo(() => {
      if (isLiveChatActive) return null;
      const target = getBackToPreviousStepTarget(messages);
      if (!target || !backStopMessageId) return target;
      const stopIndex = messages.findIndex((message) => message.id === backStopMessageId);
      return target.sliceEnd > stopIndex ? target : null;
    }, [isLiveChatActive, messages, backStopMessageId]);

    const canBackToPreviousStep = useMemo(() => {
      if (isLiveChatActive) return false;
      return props.hasPreviousStep ?? backTarget !== null;
    }, [isLiveChatActive, props.hasPreviousStep, backTarget]);

    const handleBackToPreviousStep = useCallback(() => {
      if (!canBackToPreviousStep) return;

      // Host-managed back navigation (e.g. `onlyLastMessage` mode) takes
      // precedence over the built-in message-slicing behaviour.
      if (onBackToPreviousStep) {
        onBackToPreviousStep();
        return;
      }

      const target = backTarget;
      if (!target) return;

      setMessages((prev) => prev.slice(0, target.sliceEnd));

      if (target.resetMessageKey) {
        const resetMessageKey = target.resetMessageKey;

        setMessageResetKeys((prev) => ({
          ...prev,
          [resetMessageKey]: (prev[resetMessageKey] ?? 0) + 1,
        }));
      }
    }, [onBackToPreviousStep, canBackToPreviousStep, backTarget, setMessages]);

    const getMessageResetKey = useCallback(
      (messageKey?: string) => (messageKey ? messageResetKeys[messageKey] ?? 0 : 0),
      [messageResetKeys]
    );

    return (
      <ChatContext.Provider
        value={{
          ...props,
          chatbotParams,
          selectedLocale: selectedLocaleValues,
          defaultLocale: defaultLocaleValues,
          footerContent,
          setFooterContent,
          buttonsContent,
          setButtonsContent,
          canBackToPreviousStep,
          handleBackToPreviousStep,
          getMessageResetKey,
          lastClickedButton,
          setLastClickedButton,
          revealedMessageKeysRef,
          lastUserMessageId,
          lastUserMessageRef,
          messagesListRef,
        }}
      >
        {children}
      </ChatContext.Provider>
    );
  }
);

ChatProvider.displayName = "ChatProvider";
